import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useClickOutside } from 'primereact/hooks';
import { InputText } from 'primereact/inputtext';
import { UseFormSetValue } from 'react-hook-form';
import { AssetForEdit, FiasObject } from 'types/interfaces';

import './styles/FiasSearchInput.css';

interface FiasSearchInputProps {
  value: FiasObject | null;
  fieldName: keyof AssetForEdit; // Имя поля, которое будет обновляться
  setValue: UseFormSetValue<AssetForEdit>;
  parentId?: number;
  placeholder: string;
  searchUrl: string;
  disabled?: boolean;
  dependentFields: Array<keyof AssetForEdit>; // Указываем, что это массив ключей из AssetForEdit
  isCity?: boolean;
  className?: string;
}

const FiasSearchInput = ({
  value,
  fieldName,
  setValue,
  parentId,
  placeholder,
  searchUrl,
  disabled,
  dependentFields,
  isCity = false,
  className
}: FiasSearchInputProps) => {
  const [inputValue, setInputValue] = useState(() => {
    return value ? (isCity ? value.fullName || value.name : value.name) : '';
  });
  const [results, setResults] = useState<FiasObject[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  // eslint-disable-next-line no-undef
  const debounceTimeout = useRef<ReturnType<typeof setTimeout>>();
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInputValue(getDisplayValue(value));
  }, [value, isCity]);

  const fetchResults = useCallback(
    async (query: string) => {
      try {
        if (query.length < 3) {
          setResults([]);
          return;
        }

        setIsLoading(true);
        let url = searchUrl;

        if (parentId) {
          url = url.replace('{parentId}', parentId.toString());
        }

        const response = await fetch(
          `${url}?name=${encodeURIComponent(query)}`
        );

        if (!response.ok) throw new Error('Ошибка сети');

        const data = await response.json();
        setResults(data);
      } catch (error) {
        console.error('Ошибка поиска:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [parentId, searchUrl]
  );

  const debouncedSearch = useCallback(
    (query: string) => {
      clearTimeout(debounceTimeout.current);
      debounceTimeout.current = setTimeout(() => fetchResults(query), 500);
    },
    [fetchResults]
  );

  const resetDependentFields = () => {
    dependentFields.forEach((field) =>
      setValue(field, null as any, { shouldDirty: true })
    );
  };

  const getDisplayValue = (item: FiasObject | null) => {
    if (!item) return '';
    return isCity ? item.fullName || item.name : item.name;
  };

  const updateFieldValue = (
    newValue: FiasObject | null,
    shouldValidate = false
  ) => {
    setValue(fieldName, newValue as any, {
      shouldDirty: true,
      shouldValidate
    });
    setInputValue(getDisplayValue(newValue));
    setIsOpen(false);
    resetDependentFields();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;

    if (inputValue && value) {
      updateFieldValue(
        query.length > 0
          ? {
            ...value,
            ...(isCity ? { fullName: query } : { name: query }),
            ...{ objectId: 0 }
          }
          : null
      );
    }

    setInputValue(query);
    debouncedSearch(query);
    setIsOpen(true);
  };

  const handleSelect = (item: FiasObject) => {
    updateFieldValue(item, true);
  };

  const handleClear = () => {
    updateFieldValue(null);
    setInputValue('');
  };

  useClickOutside(wrapperRef, () => {
    setIsOpen(false);
  });

  return (
    <div className={className ?? 'fias-search-wrapper'} ref={wrapperRef}>
      <div className="input-container">
        <InputText
          autoComplete={JSON.stringify(inputValue)}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full"
        />
        {inputValue && (
          <button type="button" onClick={handleClear} className="clear-button">
            <i className="pi pi-times"></i>
          </button>
        )}
      </div>

      {isOpen && (
        <div
          className={' dropdown-results'}
          style={{
            width: wrapperRef.current?.clientWidth
              ? `${wrapperRef.current.clientWidth}px`
              : '100%'
          }}
        >
          {isLoading ? (
            <div className="loading-message">Загрузка...</div>
          ) : results.length > 0 ? (
            results.map((item) => (
              <div
                key={item.objectId}
                className="result-item"
                onClick={() => handleSelect(item)}
              >
                {getDisplayValue(item)}
              </div>
            ))
          ) : (
            <div className="no-results">Ничего не найдено</div>
          )}
        </div>
      )}
    </div>
  );
};

export default FiasSearchInput;
